"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { useLabsApiAuth } from "@/lib/labsIdentityContext";

export default function AuthHeader() {
  const { user, loading: authLoading, refreshAuth } = useAuth();
  const { profile, loading: identityLoading } = useLabsApiAuth();

  const pathname = usePathname() ?? "";
  const isExperimentsActive = pathname === "/experiments" || pathname.startsWith("/experiments/");
  const isAdminActive = pathname === "/admin" || pathname.startsWith("/admin/");

  const loading = authLoading || (user != null && identityLoading);

  const displayName =
    profile?.displayName ||
    (user
      ? user.firstName
        ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ""}`
        : user.email || "Signed in"
      : null);

  const isAdmin = profile?.isAdmin ?? false;
  const userDisabled = profile?.isDisabled ?? false;
  const hasSiteAccess = isAdmin || (profile?.siteAccessApproved ?? false);

  const navLinkClass = (active: boolean) =>
    `text-sm font-medium rounded-md px-2 py-1.5 ${
      active
        ? "bg-white/10 text-white"
        : "text-white/80 hover:text-white hover:bg-white/10"
    }`;

  const handleSignIn = () => {
    void refreshAuth({ ensureSignedIn: true });
  };

  return (
    <header className="border-b border-white/10 bg-[var(--stratus-navy-90)]">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-[auto_1fr] items-center gap-2 px-6 py-4 sm:px-8 lg:px-10">
        <Link href="/" className="justify-self-start" aria-label="Stratus Labs home">
          <img
            src="/Stratus_Labs_Logo_White.svg"
            alt="Stratus Labs"
            className="h-6 w-auto hover:opacity-90"
          />
        </Link>
        <nav className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
          {loading ? (
            <span className="text-sm text-white/70">Loading…</span>
          ) : user ? (
            <>
              {userDisabled ? (
                <span className="rounded-md bg-amber-100 px-2 py-1.5 text-sm font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  Account disabled
                </span>
              ) : !hasSiteAccess ? (
                <span className="rounded-md bg-amber-100 px-2 py-1.5 text-sm font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  Access pending approval
                </span>
              ) : (
                <>
                  <Link href="/experiments/" className={navLinkClass(isExperimentsActive)}>
                    Experiments
                  </Link>
                  {isAdmin && (
                    <Link href="/admin/" className={navLinkClass(isAdminActive)}>
                      Admin
                    </Link>
                  )}
                </>
              )}
              <span className="text-sm text-white/80" title={profile?.email || user.email || undefined}>
                {displayName}
              </span>
              <a
                href="/auth/sign-out"
                className="rounded-md border border-white/30 bg-white/10 px-4 py-2 text-sm font-medium text-white hover:bg-white/20"
              >
                Sign out
              </a>
            </>
          ) : (
            <button
              type="button"
              onClick={handleSignIn}
              className="inline-flex items-center rounded-lg bg-[var(--stratus-primary)] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--stratus-primary)] focus:ring-offset-2 focus:ring-offset-[var(--stratus-navy-90)]"
            >
              Sign in
            </button>
          )}
        </nav>
      </div>
    </header>
  );
}
